// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback } from 'react';
import { useEffect, useRef } from 'react';
import { useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { singleSelection } from '../../internal/components/listbox/selection/single-selection';
import { useListbox } from '../../internal/components/listbox/use-listbox';
import { useTriggerKeyboard } from '../../internal/components/options-list/utils/use-keyboard';
import { useOpenState } from '../../internal/components/options-list/utils/use-open-state';
import { fireNonCancelableEvent } from '../../internal/events';
import useForwardFocus from '../../internal/hooks/forward-focus';
import { usePrevious } from '../../internal/hooks/use-previous';
export function useSelect({ selectedOptions, updateSelectedOption, options, filteringType, onBlur, onFocus, externalRef, keepOpen, embedded, fireLoadItems, setFilteringValue, useInteractiveGroups = false, statusType, selection = singleSelection(selectedOptions), toggleAll, dropdownRole, getParent = () => undefined, selectedAriaLabel, renderHighlightedAriaLive, }) {
    const filterRef = useRef(null);
    const triggerRef = useRef(null);
    const hasFilter = filteringType !== 'none' && !embedded;
    // Filtering always renders a dialog; `dropdownRole` can additionally force dialog semantics without a filter.
    const isDialog = hasFilter || dropdownRole === 'dialog';
    // `onSelect` and `onEscape` run only on user events, after `closeDropdownIfNecessary` and `handleEscape` below
    // are declared.
    const listbox = useListbox({
        options,
        selectedOptions,
        selection,
        interactiveGroups: useInteractiveGroups,
        onSelect: option => {
            if (option.type === 'select-all' && toggleAll) {
                toggleAll();
            }
            else {
                updateSelectedOption(option.option);
            }
            closeDropdownIfNecessary();
        },
        // With a filter input, Space types into it unless an option has a keyboard highlight.
        spaceSelects: hasFilter ? 'keyboard-highlight' : 'always',
        onEscape: () => handleEscape(),
        getParent,
        selectedAriaLabel,
        renderHighlightedAriaLive,
    });
    const activeRef = hasFilter ? filterRef : listbox.listRef;
    const { listId: menuId, highlightType, highlightedOption, highlightedIndex, selectedOptions: __selectedOptions, highlight: { resetHighlightWithKeyboard, setHighlightedIndexWithMouse, highlightOptionWithKeyboard, highlightFirstOptionWithMouse, goHomeWithKeyboard, }, } = listbox;
    const { isOpen, openDropdown, closeDropdown, toggleDropdown, openedWithKeyboard } = useOpenState({
        defaultOpen: embedded,
        onOpen: () => fireLoadItems(''),
        onClose: () => {
            resetHighlightWithKeyboard();
            setFilteringValue === null || setFilteringValue === void 0 ? void 0 : setFilteringValue('');
        },
    });
    const handleFocus = () => {
        fireNonCancelableEvent(onFocus, {});
    };
    const handleBlur = () => {
        fireNonCancelableEvent(onBlur, {});
        closeDropdown();
    };
    const hasSelectedOption = __selectedOptions.length > 0;
    const dialogId = useUniqueId('dialog');
    const closeDropdownIfNecessary = () => {
        var _a;
        if (!keepOpen) {
            (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            closeDropdown();
        }
    };
    const handleEscape = () => {
        var _a;
        if (!embedded) {
            (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            closeDropdown();
        }
    };
    const triggerKeyDownHandler = useTriggerKeyboard({
        openDropdown: () => openDropdown(true),
        goHome: goHomeWithKeyboard,
    });
    const getDropdownProps = () => ({
        onFocus: handleFocus,
        onBlur: handleBlur,
        dropdownContentId: dialogId,
        ariaRole: isDialog ? 'dialog' : undefined,
    });
    const getTriggerProps = (disabled = false, autoFocus = false) => {
        const triggerProps = {
            ref: triggerRef,
            onFocus: () => closeDropdown(),
            autoFocus,
            ariaHasPopup: isDialog ? 'dialog' : 'listbox',
            ariaControls: isOpen ? (isDialog ? dialogId : menuId) : undefined,
        };
        if (!disabled) {
            triggerProps.onMouseDown = (event) => {
                var _a;
                event.preventDefault(); // prevent current focus from blurring as it immediately closes the dropdown
                if (isOpen) {
                    (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
                toggleDropdown();
            };
            triggerProps.onKeyDown = triggerKeyDownHandler;
        }
        return triggerProps;
    };
    const getFilterProps = () => {
        if (!hasFilter || !setFilteringValue) {
            return {};
        }
        return {
            ref: filterRef,
            onKeyDown: listbox.onKeyDown,
            onChange: event => {
                setFilteringValue(event.detail.value);
                resetHighlightWithKeyboard();
            },
            __onDelayedInput: event => {
                fireLoadItems(event.detail.value);
            },
            nativeInputAttributes: {
                'aria-activedescendant': listbox.activeDescendantId,
                ['aria-owns']: menuId,
                ['aria-controls']: menuId,
            },
        };
    };
    const getMenuProps = () => {
        const menuProps = {
            ...listbox.getListProps({ withKeyboard: !hasFilter, embedded }),
            open: isOpen,
            statusType,
        };
        return menuProps;
    };
    const prevOpen = usePrevious(isOpen);
    useEffect(() => {
        // highlight the first selected option, when opening the Select component without filter input
        // keep the focus in the filter input when opening, so that screenreader can recognize the combobox
        if (isOpen && !prevOpen && options.length > 0 && !hasFilter) {
            if (openedWithKeyboard) {
                if (__selectedOptions[0]) {
                    highlightOptionWithKeyboard(__selectedOptions[0]);
                }
                else {
                    goHomeWithKeyboard();
                }
            }
            else {
                if (!__selectedOptions[0] || !options.includes(__selectedOptions[0])) {
                    highlightFirstOptionWithMouse();
                }
                else {
                    const highlightedIndex = options.indexOf(__selectedOptions[0]);
                    setHighlightedIndexWithMouse(highlightedIndex, true);
                }
            }
        }
    }, [
        isOpen,
        __selectedOptions,
        hasSelectedOption,
        setHighlightedIndexWithMouse,
        highlightOptionWithKeyboard,
        highlightFirstOptionWithMouse,
        goHomeWithKeyboard,
        openedWithKeyboard,
        options,
        prevOpen,
        hasFilter,
    ]);
    const focusActiveRef = useCallback(() => {
        var _a;
        // dropdown-fit calculations ensure that the dropdown will fit inside the current
        // viewport, so prevent the browser from trying to scroll it into view (e.g. if
        // scroll-padding-top is set on a parent)
        (_a = activeRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
    }, [activeRef]);
    useEffect(() => {
        if (isOpen && !embedded) {
            focusActiveRef();
        }
    }, [isOpen, activeRef, embedded, focusActiveRef]);
    useForwardFocus(externalRef, triggerRef);
    // Closes the dropdown and returns focus to the trigger, matching the normal close behavior.
    // Exposed to the public dropdown-customization render props via `DropdownContentProps.closeDropdown`.
    // Focusing the trigger fires its onFocus handler, which already closes the dropdown; only close
    // explicitly when the trigger is already focused (re-focusing it does not fire onFocus), so the
    // close side effects run exactly once.
    const closeDropdownAndRefocus = useCallback(() => {
        const trigger = triggerRef.current;
        if (trigger && trigger === document.activeElement) {
            closeDropdown();
        }
        else {
            trigger === null || trigger === void 0 ? void 0 : trigger.focus();
        }
    }, [closeDropdown]);
    return {
        isOpen,
        highlightedOption,
        highlightedIndex,
        highlightType,
        getTriggerProps,
        getDropdownProps,
        getMenuProps,
        getFilterProps,
        getOptionProps: listbox.getOptionProps,
        highlightOption: highlightOptionWithKeyboard,
        selectOption: listbox.selectOption,
        announceSelected: listbox.announceSelected,
        announcement: listbox.announcement,
        scrollToIndex: listbox.scrollToIndex,
        closeDropdown: closeDropdownAndRefocus,
        dialogId,
        focusActiveRef,
    };
}
//# sourceMappingURL=use-select.js.map